import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import React__default, { PureComponent } from 'react';
import styled from 'styled-components';
import PT from 'prop-types';
import { $ as $f7dceffc5ad7768b$export$4e328f61c538687f } from '../3rdPartyModules/_react-aria/focus/dist/useFocusRing-BO5gj-B1.js';
import { $ as $3ef42575df84b30b$export$9d1611c77c2fe928 } from '../3rdPartyModules/_react-aria/utils/dist/mergeProps-DMtmc2Zw.js';
import { f as focusOutline } from '../FocusIndicator/styleMixins/focusOutline-DNwtedBp.js';
import { a as TaktIdProvider, T as TaktIdConsumer } from '../TaktIdContext/TaktIdContext-DlOuPWuU.js';
import { c as createStormTaktId } from '../TaktIdContext/TaktIdContext.utils-DaCk9T1Y.js';
import { I as Icon } from '../Icon/Icon-CpzUnHit.js';
import { w as angleDoubleRight, v as angleDoubleLeft } from '../LocalModules/storm-ui-icons/dist/esm/localIcons-EM4gOTGb-B7bhIt-r.js';
import { T as Text } from '../Text/Text-nnmbpLTK.js';

const NavWrapper = /*#__PURE__*/styled.nav.withConfig({
  componentId: "sc-storm-ui-30060407__sc-ham2kp-0"
})(["position:relative;"]);
NavWrapper.displayName = 'NavWrapper';
const NavContainer = /*#__PURE__*/styled('ul').withConfig({
  componentId: "sc-storm-ui-30060407__sc-ham2kp-1"
})(["padding:0;margin:0;width:", ";height:100%;background-color:", ";border-right:", ";box-sizing:border-box;padding-top:", ";margin-left:0px;display:inline-block;"], ({
  $width,
  theme
}) => $width || theme.sideNav.width, ({
  theme
}) => theme.sideNav.bg, ({
  theme
}) => theme.sideNav.borderRight, ({
  theme
}) => theme.sideNav.paddingTop);
NavContainer.displayName = 'NavContainer';
const NavExpandToggle = /*#__PURE__*/styled('button').attrs(({
  onFocus,
  onBlur
}) => {
  const {
    isFocusVisible,
    focusProps
  } = $f7dceffc5ad7768b$export$4e328f61c538687f();
  return {
    /*
     *`useFocusRing()` uses `onFocus` and `onBlur` props, so `mergeProps()` must be used to
     * make sure user supplied `onFocus` and `onBlur` are also called.
    */
    ...$3ef42575df84b30b$export$9d1611c77c2fe928({
      onFocus,
      onBlur
    }, focusProps),
    focusVisible: isFocusVisible,
    type: 'button'
  };
}).withConfig({
  componentId: "sc-storm-ui-30060407__sc-ham2kp-2"
})(["background-color:transparent;display:inline-block;position:absolute;vertical-align:top;top:120px;width:0;height:34px;/*! @noflip */ border-left:14px solid #E6E9ED;/*! @noflip */ border-right:0;border-top:8px solid transparent;border-bottom:8px solid transparent;padding:0;cursor:pointer;:active{color:inherit;}:focus{outline:none;", "}/*! @noflip */ [dir=\"rtl\"] &&{transform:scaleX(-1);}"], ({
  focusVisible
}) => focusVisible && focusOutline);
NavExpandToggle.displayName = 'NavExpandToggle';
const NavIconContainer = /*#__PURE__*/styled.span.withConfig({
  componentId: "sc-storm-ui-30060407__sc-ham2kp-3"
})(["position:absolute;/*! @noflip */ left:-12px;/*! @noflip */ right:0;top:0;"]);
NavIconContainer.displayName = 'NavIconContainer';
class SideNav extends PureComponent {
  constructor(props) {
    super(props);
    _defineProperty(this, "handleToggleCollapse", () => {
      this.setState(({
        isCollapsed
      }) => ({
        isCollapsed: !isCollapsed
      }));
      const {
        onToggleCollapse
      } = this.props;
      if (typeof onToggleCollapse === 'function') {
        const {
          isCollapsed
        } = this.state;
        onToggleCollapse(!isCollapsed);
      }
    });
    this.state = {
      isCollapsed: props.isInitiallyCollapsed
    };
  }
  render() {
    const {
      taktId,
      taktValue
    } = this.props;
    const {
      children,
      width,
      onToggleCollapse,
      isInitiallyCollapsed,
      expandScreenReaderLabel = 'Show navigation menu',
      collapseScreenReaderLabel = 'Hide navigation menu',
      ariaLabel = 'Sidebar',
      ...rest
    } = this.props;
    const {
      isCollapsed
    } = this.state;
    return /*#__PURE__*/React__default.createElement(TaktIdProvider, {
      taktId: taktId,
      taktValue: taktValue,
      fallbackId: createStormTaktId('side-nav')
    }, /*#__PURE__*/React__default.createElement(NavWrapper, _extends({}, rest, {
      "aria-label": ariaLabel
    }), !isCollapsed && /*#__PURE__*/React__default.createElement(NavContainer, {
      $width: width
    }, children), onToggleCollapse && /*#__PURE__*/React__default.createElement(TaktIdConsumer, {
      fallbackId: createStormTaktId('expander-toggle')
    }, ({
      getDataTaktAttributes
    }) => /*#__PURE__*/React__default.createElement(NavExpandToggle, _extends({
      onClick: this.handleToggleCollapse,
      "aria-expanded": !isCollapsed
    }, getDataTaktAttributes()), /*#__PURE__*/React__default.createElement(NavIconContainer, null, isCollapsed && /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Icon, {
      type: angleDoubleRight,
      size: "xs"
    }), /*#__PURE__*/React__default.createElement(Text, {
      type: "sr-only"
    }, expandScreenReaderLabel)), !isCollapsed && /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Icon, {
      type: angleDoubleLeft,
      size: "xs"
    }), /*#__PURE__*/React__default.createElement(Text, {
      type: "sr-only"
    }, collapseScreenReaderLabel)))))));
  }
}
_defineProperty(SideNav, "propTypes", {
  /**
   * What to display in the side nav.
   */
  children: PT.node.isRequired,
  /**
   * The desired width of the nav container.
   */
  width: PT.string,
  /**
   * Hide the side nav by default with `true`.
   */
  isInitiallyCollapsed: PT.bool,
  /**
   * If provided, an expander button is shown. When clicked it will call the callback.
   */
  onToggleCollapse: PT.func,
  /**
   * Provide a localized screen reader label for the expander button. [See string translations for the default value](https://translator.amazon.com/search/strings?exact_match&tag=stormui_hide_nav_menu)
   */
  collapseScreenReaderLabel: PT.string,
  /**
   * Provide a localized screen reader label for the expander button. [See string translations for the default value](https://translator.amazon.com/search/strings?exact_match&tag=stormui_show_nav_menu)
   */
  expandScreenReaderLabel: PT.string,
  /**
   * Provide a localized screen reader label for the navigation landmark.
   */
  ariaLabel: PT.string
});
_defineProperty(SideNav, "defaultProps", {
  width: null,
  isInitiallyCollapsed: false,
  onToggleCollapse: null,
  collapseScreenReaderLabel: 'Hide navigation menu',
  expandScreenReaderLabel: 'Show navigation menu',
  ariaLabel: 'Sidebar'
});

export { SideNav as S };
//# sourceMappingURL=SideNav-pGTN_KNv.js.map
